<script lang="ts">
  interface Sample {
    used_name: string
    video_id: string
    timecode: number
  }

  interface VideoCount {
    video_id: string
    count: number
  }

  interface Detail {
    id: number
    name: string
    aliases: string[]
    description: string | null
    internal_only: boolean
    total_mentions: number
    mentions_per_video: VideoCount[]
    samples: Sample[]
  }

  type Kind = 'person' | 'topic'

  function readParams(): { kind: Kind; winnerId: number | null; loserId: number | null } {
    const params = new URLSearchParams(window.location.search)
    const kindParam = params.get('type')
    const kind: Kind = kindParam === 'topic' ? 'topic' : 'person'
    const winnerId = Number(params.get('winner')) || null
    const loserId = Number(params.get('loser')) || null
    return { kind, winnerId, loserId }
  }

  const initial = readParams()
  let kind = $state<Kind>(initial.kind)
  let winnerId = $state<number | null>(initial.winnerId)
  let loserId = $state<number | null>(initial.loserId)
  let winner = $state<Detail | null>(null)
  let loser = $state<Detail | null>(null)
  let loading = $state(true)
  let error = $state('')
  let confirming = $state(false)
  let submitting = $state(false)

  const entityRoot = $derived(kind === 'topic' ? '/api/admin/topics' : '/api/admin/persons')
  const listPath = $derived(kind === 'topic' ? '/admin/topics' : '/admin/persons')

  $effect(() => {
    void winnerId
    void loserId
    loadBoth()
  })

  $effect(() => {
    if (!confirming) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelConfirm()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  async function loadBoth() {
    if (!winnerId || !loserId) {
      error = 'Missing winner or loser id in URL.'
      loading = false
      return
    }
    if (winnerId === loserId) {
      error = 'Winner and loser cannot be the same record.'
      loading = false
      winner = null
      loser = null
      return
    }
    loading = true
    error = ''
    try {
      const [winnerResp, loserResp] = await Promise.all([
        fetch(`${entityRoot}/${winnerId}`, { credentials: 'same-origin' }),
        fetch(`${entityRoot}/${loserId}`, { credentials: 'same-origin' }),
      ])
      if (!winnerResp.ok || !loserResp.ok) {
        error = `Failed to load (${winnerResp.status}/${loserResp.status})`
        return
      }
      winner = (await winnerResp.json()) as Detail
      loser = (await loserResp.json()) as Detail
    } catch (e) {
      error = 'Network error while loading the pair.'
    } finally {
      loading = false
    }
  }

  function swap() {
    const tmp = winnerId
    winnerId = loserId
    loserId = tmp
    const url = new URL(window.location.href)
    if (winnerId) url.searchParams.set('winner', String(winnerId))
    if (loserId) url.searchParams.set('loser', String(loserId))
    window.history.replaceState({}, '', url.toString())
  }

  function openConfirm() {
    confirming = true
  }

  function cancelConfirm() {
    confirming = false
  }

  async function performMerge() {
    if (!winner || !loser) return
    submitting = true
    error = ''
    try {
      const response = await fetch('/api/admin/merges', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: kind, winner_id: winner.id, loser_id: loser.id }),
      })
      if (response.ok) {
        window.location.href = listPath
        return
      }
      const payload = await response.json().catch(() => ({}))
      error = payload?.error ? `Merge failed: ${payload.error}` : `Merge failed (${response.status})`
    } catch (e) {
      error = 'Network error during merge.'
    } finally {
      submitting = false
    }
  }
</script>

<div class="console">
  <header class="console-header">
    <div class="header-label">
      <div class="label-primary">MERGE / {kind.toUpperCase()}</div>
      <div class="label-secondary">SIDE BY SIDE COMPARE</div>
    </div>
    <div class="header-actions">
      <button type="button" class="ghost-btn" onclick={swap} disabled={loading}>SWAP WINNER/LOSER</button>
      <a class="back-btn" href={listPath}>BACK</a>
    </div>
  </header>

  {#if loading}
    <div class="status pulse">LOADING PAIR…</div>
  {:else if error}
    <div class="status error">{error.toUpperCase()}</div>
  {:else if winner && loser}
    <section class="compare-grid">
      <article class="compare-column winner">
        <div class="role-tag winner-tag">WINNER (KEEPS)</div>
        {@render entityCard(winner)}
      </article>
      <article class="compare-column loser">
        <div class="role-tag loser-tag">LOSER (DESTROYED)</div>
        {@render entityCard(loser)}
      </article>
    </section>

    <div class="action-bar">
      <button type="button" class="primary-btn" onclick={openConfirm}>
        MERGE {loser.name} INTO {winner.name}
      </button>
    </div>
  {/if}
</div>

{#snippet entityCard(entity: Detail)}
  <h2 class="entity-name">{entity.name}</h2>
  <dl class="entity-meta">
    <dt>ID</dt>
    <dd>{entity.id}</dd>
    <dt>Aliases</dt>
    <dd>{entity.aliases.length === 0 ? 'none' : entity.aliases.join(', ')}</dd>
    <dt>Description</dt>
    <dd>{entity.description || 'none'}</dd>
    <dt>Internal only</dt>
    <dd>{entity.internal_only ? 'YES' : 'NO'}</dd>
    <dt>Total mentions</dt>
    <dd>{entity.total_mentions}</dd>
    <dt>Per video</dt>
    <dd>
      {#if entity.mentions_per_video.length === 0}
        none
      {:else}
        <ul class="video-list">
          {#each entity.mentions_per_video as v (v.video_id)}
            <li><span class="video-id">{v.video_id}</span> · <span>{v.count}</span></li>
          {/each}
        </ul>
      {/if}
    </dd>
    <dt>Samples</dt>
    <dd>
      {#if entity.samples.length === 0}
        none
      {:else}
        <ul class="sample-list">
          {#each entity.samples as s (s.video_id + '-' + s.timecode)}
            <li>
              <span class="used-name">"{s.used_name}"</span>
              <span class="sample-meta">{s.video_id} @ {s.timecode.toFixed(1)}s</span>
            </li>
          {/each}
        </ul>
      {/if}
    </dd>
  </dl>
{/snippet}

{#if confirming && winner && loser}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={cancelConfirm}
  >
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title" onclick={(e) => e.stopPropagation()}>
      <h3 id="confirm-title">CONFIRM MERGE</h3>
      <p>
        <strong>{loser.name}</strong> will be merged into <strong>{winner.name}</strong>.
        All {loser.total_mentions} mention{loser.total_mentions === 1 ? '' : 's'} will be repointed,
        aliases will be combined, and the loser record will be destroyed. This is irreversible.
      </p>
      {#if error}
        <p class="modal-error">{error}</p>
      {/if}
      <div class="modal-actions">
        <button type="button" class="ghost-btn" onclick={cancelConfirm} disabled={submitting}>
          CANCEL
        </button>
        <button type="button" class="danger-btn" onclick={performMerge} disabled={submitting}>
          {submitting ? 'MERGING…' : 'YES, MERGE'}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .console {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: var(--color-surface-0);
    padding: 2rem;
    font-family: var(--font-mono);
  }

  .console-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .header-label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .label-primary {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
  }

  .label-secondary {
    font-size: 0.75rem;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }

  .back-btn,
  .ghost-btn {
    padding: 0.5rem 1rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-family: inherit;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-decoration: none;
    cursor: pointer;
  }

  .back-btn:hover,
  .ghost-btn:hover {
    border-color: var(--color-accent-primary);
  }

  .ghost-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .status {
    padding: 2rem;
    font-size: 0.875rem;
    letter-spacing: 0.1em;
    color: var(--color-text-secondary);
  }

  .status.error {
    color: var(--color-accent-playhead);
  }

  .status.pulse {
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .compare-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .compare-column {
    padding: 1.25rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
  }

  .role-tag {
    display: inline-block;
    padding: 0.2rem 0.5rem;
    margin-bottom: 0.75rem;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    border: 1px solid;
  }

  .winner-tag {
    color: var(--color-accent-primary);
    border-color: var(--color-accent-primary);
  }

  .loser-tag {
    color: var(--color-accent-playhead);
    border-color: var(--color-accent-playhead);
  }

  .entity-name {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 1rem;
    color: var(--color-text-primary);
  }

  .entity-meta {
    display: grid;
    grid-template-columns: 8rem 1fr;
    gap: 0.5rem 0.75rem;
    margin: 0;
    font-size: 0.75rem;
  }

  .entity-meta dt {
    color: var(--color-text-muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .entity-meta dd {
    margin: 0;
    color: var(--color-text-primary);
  }

  .video-list,
  .sample-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .video-id {
    color: var(--color-text-secondary);
  }

  .sample-list li {
    display: flex;
    flex-direction: column;
  }

  .used-name {
    color: var(--color-text-primary);
  }

  .sample-meta {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .action-bar {
    display: flex;
    justify-content: center;
    padding: 1.5rem 0;
  }

  .primary-btn {
    padding: 0.875rem 1.75rem;
    background: var(--color-accent-primary);
    border: 1px solid var(--color-accent-primary);
    color: var(--color-surface-0);
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    cursor: pointer;
  }

  .primary-btn:hover {
    filter: brightness(1.1);
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .modal {
    max-width: 480px;
    width: calc(100% - 2rem);
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-strong);
    padding: 1.5rem;
    font-family: var(--font-mono);
  }

  .modal h3 {
    margin: 0 0 1rem;
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--color-accent-primary);
  }

  .modal p {
    margin: 0 0 1.25rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--color-text-primary);
  }

  .modal-error {
    padding: 0.5rem 0.75rem;
    background: color-mix(in srgb, var(--color-accent-playhead) 15%, transparent);
    border: 1px solid var(--color-accent-playhead);
    color: var(--color-accent-playhead);
    font-size: 0.75rem;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .danger-btn {
    padding: 0.5rem 1rem;
    background: var(--color-accent-playhead);
    border: 1px solid var(--color-accent-playhead);
    color: var(--color-surface-0);
    font-family: inherit;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    cursor: pointer;
  }

  .danger-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
